import React from 'react';
import { Helmet } from 'react-helmet-async';
import seoConfig from '../config/seoConfig';

const SEOHead = ({ 
  title = '股票信号网 - A股龙虎榜数据分析平台 | 港股美股实时行情 | 金融工具箱',
  description = 'A股龙虎榜数据分析平台，提供实时港股美股行情，专业金融工具包含仓位管理计算器、风险评估工具。专注股票投资分析，助力投资决策。',
  keywords = 'A股龙虎榜,港股行情,美股数据,股票分析,仓位管理,金融工具,投资计算器,股票信号,实时行情,投资分析,风险管理,股票数据',
  url = 'https://stocksignals.cn',
  image = 'https://stocksignals.cn/og-image.jpg'
}) => {
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "股票信号网",
    "alternateName": "StockSignals",
    "url": url,
    "description": description,
    "potentialAction": {
      "@type": "SearchAction",
      "target": {
        "@type": "EntryPoint",
        "urlTemplate": `${url}/search?q={search_term_string}`
      },
      "query-input": "required name=search_term_string"
    },
    "publisher": {
      "@type": "Organization",
      "name": "股票信号网",
      "url": url,
      "logo": {
        "@type": "ImageObject",
        "url": `${url}/logo.png`
      }
    },
    "mainEntity": {
      "@type": "FinancialService",
      "name": "股票数据分析服务",
      "description": "提供A股龙虎榜、港股美股实时数据分析和金融工具服务",
      "serviceType": "金融数据分析",
      "areaServed": "中国",
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": "金融工具服务",
        "itemListElement": [
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "A股龙虎榜数据分析",
              "description": "实时A股龙虎榜数据查询和分析"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "港股美股行情",
              "description": "实时港股美股行情数据"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "仓位管理计算器",
              "description": "专业的投资仓位管理和风险控制工具"
            }
          }
        ]
      }
    }
  };

  return (
    <Helmet>
      {/* 基础SEO标签 */}
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="author" content="股票信号网" />
      <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
      <meta name="googlebot" content="index, follow" />
      <meta name="bingbot" content="index, follow" />
      <meta name="baiduspider" content="index, follow" />
      
      {/* 语言和地区 */}
      <meta name="language" content="zh-CN" />
      <meta name="geo.region" content="CN" />
      <meta name="geo.country" content="China" />
      <meta name="geo.placename" content="China" />
      
      {/* Open Graph 标签 (Facebook, LinkedIn等) */}
      <meta property="og:type" content="website" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content="股票信号网" />
      <meta property="og:locale" content="zh_CN" />
      
      {/* Twitter Card 标签 */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      <meta name="twitter:site" content="@stocksignals" />
      
      {/* 百度特定的SEO标签 */}
      <meta name="baidu-site-verification" content={seoConfig.baidu.siteVerification} />
      <meta name="applicable-device" content="pc,mobile" />
      <meta name="MobileOptimized" content="width" />
      <meta name="HandheldFriendly" content="true" />
      
      {/* Google特定标签 */}
      <meta name="google-site-verification" content={seoConfig.google.siteVerification} />
      <meta name="theme-color" content="#1976d2" />
      
      {/* 移动端优化 */}
      <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=5.0, user-scalable=yes" />
      <meta name="format-detection" content="telephone=no" />
      
      {/* 缓存控制 */}
      <meta httpEquiv="Cache-Control" content="public, max-age=31536000" />
      
      {/* Canonical URL */}
      <link rel="canonical" href={url} />
      
      {/* 预连接重要域名 */}
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
      <link rel="preconnect" href="https://www.google-analytics.com" />
      
      {/* DNS预解析 */}
      <link rel="dns-prefetch" href="//fonts.googleapis.com" />
      <link rel="dns-prefetch" href="//fonts.gstatic.com" />
      <link rel="dns-prefetch" href="//www.google-analytics.com" />
      <link rel="dns-prefetch" href="//www.googletagmanager.com" />
      
      {/* 结构化数据 */}
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>
      
      {/* 百度统计代码位置预留 */}
      {/* 可以在这里添加百度统计代码 */}
      
      {/* 额外的meta标签用于搜索引擎优化 */}
      <meta name="rating" content="general" />
      <meta name="distribution" content="global" />
      <meta name="revisit-after" content="1 days" />
      <meta name="expires" content="never" />
      
      {/* 社交媒体标签 */}
      <meta property="article:author" content="股票信号网" />
      <meta property="article:publisher" content="https://stocksignals.cn" />
      
      {/* 移动应用相关 */}
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="default" />
      <meta name="apple-mobile-web-app-title" content="股票信号网" />
    </Helmet>
  );
};

export default SEOHead;